import type { GeometryStatus } from '../types/BaseGeometry';
import type { GeometrySearchFilters, GeometrySearchResult } from '../types/GeometrySearch';
import type { Point } from '../types/Point';
import type { Polygon } from '../types/Polygon';
import type { RIMSPolyline } from '../types/Polylines';

export interface FilteredGeometrySearchResult {
  results: GeometrySearchResult[];
  polylines: RIMSPolyline[];
  points: Point[];
  polygons: Polygon[];
}

export function buildGeometrySearchResults(
  allPolylines: RIMSPolyline[],
  allPoints: Point[],
  allPolygons: Polygon[],
): GeometrySearchResult[] {
  const results: GeometrySearchResult[] = [];

  allPolylines.forEach((polyline) => {
    results.push({
      id: polyline.id,
      featureId: polyline.featureId,
      name: polyline.name,
      type: 'polyline',
      contractId: polyline.contractId,
      contractName: polyline.contractName,
      foremanId: polyline.foremanId,
      foremanName: polyline.foremanName,
      startDate: polyline.startDate,
      statusDesc: polyline.statusDesc,
      wkt: polyline.wkt,
      addressSystemId: polyline.addressSystemId,
      fullGeometry: polyline,
    });
  });

  allPoints.forEach((point) => {
    results.push({
      id: point.id,
      featureId: point.featureId,
      name: point.name,
      type: 'point',
      contractId: point.contractId,
      contractName: point.contractName,
      foremanId: point.foremanId,
      foremanName: point.foremanName,
      startDate: point.startDate,
      statusDesc: point.statusDesc,
      wkt: point.wkt,
      addressSystemId: point.addressSystemId,
      fullGeometry: point,
    });
  });

  allPolygons.forEach((polygon) => {
    results.push({
      id: polygon.id,
      featureId: polygon.featureId,
      name: polygon.name,
      type: 'polygon',
      contractId: polygon.contractId,
      contractName: polygon.contractName,
      foremanId: polygon.foremanId,
      foremanName: polygon.foremanName,
      startDate: polygon.startDate,
      statusDesc: polygon.statusDesc,
      wkt: polygon.wkt,
      addressSystemId: polygon.addressSystemId,
      fullGeometry: polygon,
    });
  });

  return results;
}

/**
 * Pure client-side geometry search filtering — shared by GeometrySearchService and tests.
 */
export function filterFetchedGeometries(
  allPolylines: RIMSPolyline[],
  allPoints: Point[],
  allPolygons: Polygon[],
  filters: GeometrySearchFilters,
  geometryStatuses: GeometryStatus[] = [],
): FilteredGeometrySearchResult {
  let filteredResults = buildGeometrySearchResults(allPolylines, allPoints, allPolygons);

  if (filters.geometryName) {
    const searchTerm = filters.geometryName.toLowerCase();
    filteredResults = filteredResults.filter((r) =>
      r.name.toLowerCase().includes(searchTerm),
    );
  }

  if (filters.foremanId) {
    filteredResults = filteredResults.filter((r) => r.foremanId === filters.foremanId);
  }

  if (filters.qualityControl1Id) {
    filteredResults = filteredResults.filter((r) => {
      const geom = r.fullGeometry as { qualityControlId?: number };
      return geom.qualityControlId === filters.qualityControl1Id;
    });
  }

  if (filters.qualityControl2Id) {
    filteredResults = filteredResults.filter((r) => {
      const geom = r.fullGeometry as { qualityControlId2?: number };
      return geom.qualityControlId2 === filters.qualityControl2Id;
    });
  }

  if (filters.fromDate) {
    filteredResults = filteredResults.filter((r) => {
      const geom = r.fullGeometry as { plannedDate?: string | null };
      const dateStr = geom.plannedDate || r.startDate;
      if (!dateStr) return false;
      return new Date(dateStr) >= filters.fromDate!;
    });
  }

  if (filters.toDate) {
    filteredResults = filteredResults.filter((r) => {
      const geom = r.fullGeometry as { plannedDate?: string | null };
      const dateStr = geom.plannedDate || r.startDate;
      if (!dateStr) return false;
      return new Date(dateStr) <= filters.toDate!;
    });
  }

  if (filters.sortBy === 'name') {
    filteredResults.sort((a, b) => a.name.localeCompare(b.name));
  } else if (filters.sortBy === 'date') {
    filteredResults.sort((a, b) => {
      const dateA = a.startDate ? new Date(a.startDate).getTime() : 0;
      const dateB = b.startDate ? new Date(b.startDate).getTime() : 0;
      return dateB - dateA;
    });
  }

  if (filters.geometryStatusId) {
    const validGeometryStatus = geometryStatuses.find(
      (status) => status.id === filters.geometryStatusId,
    );
    filteredResults = filteredResults.filter(
      (r) => r.statusDesc?.trim() === validGeometryStatus?.description.trim(),
    );
  }

  const filteredPolylines = allPolylines.filter((polyline) =>
    filteredResults.some((r) => r.type === 'polyline' && r.id === polyline.id),
  );
  const filteredPoints = allPoints.filter((point) =>
    filteredResults.some((r) => r.type === 'point' && r.id === point.id),
  );
  const filteredPolygons = allPolygons.filter((polygon) =>
    filteredResults.some((r) => r.type === 'polygon' && r.id === polygon.id),
  );

  return {
    results: filteredResults,
    polylines: filteredPolylines,
    points: filteredPoints,
    polygons: filteredPolygons,
  };
}
